import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useRestaurant } from "../context/RestaurantContext";
import { Link, useNavigate } from "react-router-dom";
import AppLayout from "../components/AppLayout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { getUpcomingDeliveries, getDrafts, deleteDraft } from "../lib/api";
import { toast } from "sonner";
import { 
  ShoppingCart, 
  ClipboardList, 
  History,
  ArrowRight,
  Wallet,
  Settings,
  Truck,
  CheckCircle,
  PackageCheck,
  FileEdit,
  Layers,
  Trash2
} from "lucide-react";

export default function DashboardPage() {
  const { user } = useAuth();
  const { currentRestaurant, isCombinedMode, restaurants, selectRestaurant, getCombinedOption } = useRestaurant();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const [upcomingDeliveries, setUpcomingDeliveries] = useState([]);
  const [drafts, setDrafts] = useState([]);

  useEffect(() => {
    if (currentRestaurant) {
      loadUpcomingDeliveries();
    }
  }, [currentRestaurant]);

  useEffect(() => {
    loadDrafts();
  }, []);

  const loadUpcomingDeliveries = async () => {
    try {
      const response = await getUpcomingDeliveries();
      setUpcomingDeliveries(response.data.slice(0, 5));
    } catch (error) {
      console.error("Failed to load upcoming deliveries:", error);
    }
  };

  const loadDrafts = async () => {
    try {
      const response = await getDrafts();
      setDrafts(response.data || []);
    } catch (error) {
      console.error("Failed to load drafts:", error);
    }
  };

  // Only show drafts belonging to the currently selected restaurant (or combined drafts in combined mode)
  const visibleDrafts = drafts.filter((d) =>
    isCombinedMode ? d.is_combined : !d.is_combined && d.restaurant_id === currentRestaurant?.id
  );

  const resumeDraft = (draft) => {
    if (draft.is_combined) {
      selectRestaurant(getCombinedOption());
    } else {
      const r = restaurants.find((x) => x.id === draft.restaurant_id);
      if (r) selectRestaurant(r);
    }
    navigate(`/create-order?draft=${draft.id}`);
  };

  const handleDeleteDraft = async (e, draft) => {
    e.stopPropagation();
    try {
      await deleteDraft(draft.id);
      setDrafts((prev) => prev.filter((d) => d.id !== draft.id));
      toast.success("Draft discarded");
    } catch (error) {
      toast.error("Failed to delete draft");
    }
  };

  // Manager links - can create orders and receive finalized orders
  const managerLinks = [
    {
      title: "Create Order",
      description: "Start a new supply order",
      icon: ShoppingCart,
      href: "/create-order",
      color: "bg-kale-100 text-kale-700",
      accent: true
    },
    {
      title: "Finalized Orders",
      description: "Receive & match deliveries",
      icon: PackageCheck,
      href: "/finalized-orders",
      color: "bg-blue-100 text-blue-700"
    },
    {
      title: "Order History",
      description: "View all orders",
      icon: History,
      href: "/order-history",
      color: "bg-stone-100 text-stone-700"
    },
  ];

  const adminLinks = [
    {
      title: "Create Order",
      description: "Start a new supply order",
      icon: ShoppingCart,
      href: "/create-order",
      color: "bg-kale-100 text-kale-700",
      accent: true
    },
    {
      title: "Submitted Orders",
      description: "Review, edit & finalize orders",
      icon: ClipboardList,
      href: "/pending-orders",
      color: "bg-amber-100 text-amber-700"
    },
    {
      title: "Finalized Orders",
      description: "Receive & match deliveries",
      icon: PackageCheck,
      href: "/finalized-orders",
      color: "bg-blue-100 text-blue-700"
    },
    {
      title: "Order History",
      description: "View all orders",
      icon: History,
      href: "/order-history",
      color: "bg-stone-100 text-stone-700"
    },
    {
      title: "Expenses",
      description: "Track spending & upload receipts",
      icon: Wallet,
      href: "/expenses",
      color: "bg-emerald-100 text-emerald-700"
    },
    {
      title: "Admin Settings",
      description: "Manage users, vendors, items & more",
      icon: Settings,
      href: "/admin-settings",
      color: "bg-stone-100 text-stone-700"
    },
  ];

  const links = isAdmin ? adminLinks : managerLinks;

  return (
    <AppLayout title="" hideTitle showRestaurantSelector>
      <div className="p-4 md:p-6 space-y-4 w-full max-w-md mx-auto">
        {/* Welcome Section with Upcoming Deliveries */}
        <div className="bg-kale-900 text-white rounded-2xl p-5 shadow-lg" data-testid="welcome-section">
          <h2 className="text-lg font-bold mb-1">
            Welcome, {user?.name}
          </h2>
          {upcomingDeliveries.length > 0 ? (
            <div className="mt-3">
              <div className="flex items-center gap-2 text-kale-200 text-xs mb-2">
                <Truck className="w-3.5 h-3.5" />
                <span>Upcoming Deliveries</span>
              </div>
              <div className="space-y-1">
                {upcomingDeliveries.map((delivery, idx) => (
                  <div 
                    key={`${delivery.vendor_id}-${delivery.day}-${idx}`}
                    className={`text-sm flex items-center justify-between ${
                      delivery.is_today ? 'text-amber-300 font-medium' : 'text-kale-100'
                    }`}
                  >
                    <span>{delivery.vendor_name}</span>
                    <span className="text-xs">
                      {delivery.is_today ? 'Today' : delivery.day.slice(0, 3)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-kale-200 text-sm">
              No upcoming deliveries scheduled
            </p>
          )}
        </div>

        {/* Draft Orders */}
        {visibleDrafts.length > 0 && (
          <div data-testid="drafts-section">
            <div className="flex items-center gap-2 mb-2 px-1">
              <FileEdit className="w-4 h-4 text-amber-600" />
              <h3 className="text-sm font-semibold text-stone-700">Draft Orders</h3>
              <span className="text-xs bg-amber-100 text-amber-700 rounded-full px-2 py-0.5 font-medium">{visibleDrafts.length}</span>
            </div>
            <div className="grid gap-2">
              {visibleDrafts.map((draft) => (
                <Card
                  key={draft.id}
                  className="card-hover border-amber-200 border cursor-pointer"
                  onClick={() => resumeDraft(draft)}
                  data-testid={`draft-card-${draft.id}`}
                >
                  <CardContent className="p-3 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
                      {draft.is_combined ? <Layers className="w-5 h-5" /> : <FileEdit className="w-5 h-5" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-stone-900 text-sm truncate">
                        {draft.is_combined ? "Combined Order" : draft.restaurant_name}
                      </h4>
                      <p className="text-xs text-stone-500 truncate">
                        {draft.item_count} item{draft.item_count === 1 ? "" : "s"} · tap to resume
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-stone-400 hover:text-red-600 flex-shrink-0"
                      onClick={(e) => handleDeleteDraft(e, draft)}
                      data-testid={`delete-draft-${draft.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Quick Actions - No heading */}
        <div className="grid gap-2">
          {links.map((link, index) => (
            <Link 
              key={link.href} 
              to={link.href}
              className={`block animate-in stagger-${index + 1}`}
              data-testid={`nav-${link.href.replace('/', '')}`}
            >
              <Card className={`card-hover ${link.accent ? 'border-kale-200 border-2' : ''}`}>
                <CardContent className="p-3 flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg ${link.color} flex items-center justify-center flex-shrink-0`}>
                    <link.icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-stone-900 text-sm">{link.title}</h4>
                    <p className="text-xs text-stone-500 truncate">{link.description}</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-stone-400 flex-shrink-0" />
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </AppLayout>
  );
}
